import { setNewPassword, verifyPasswordResetOtp } from '@/api/authApi'
import { EyeCloseIcon, EyeOpenIcon } from '@/components/Icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useMediaQueryLG } from '@/hooks/useMediaquery'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { showErrorToast } from '@/components/ui/toast'
import axios from 'axios'
import { PATHROUTES } from '@/constants/path'

const strongPasswordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(1, 'Password is required')
      .regex(/^\S+$/, 'Password must not contain spaces')
      .regex(strongPasswordRegex, {
        message:
          'Password must be at least 8 characters, include uppercase, lowercase, digit, and only allowed special characters (@ $ ! % * ? &)',
      }),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export default function CreatePassword() {
  const navigate = useNavigate()
  const isMobile = useMediaQueryLG()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const verifyMutation = useMutation({
    mutationFn: verifyPasswordResetOtp,
    onError: error => {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error?.response?.data?.message || error.message || 'An error occurred'
        showErrorToast('Link Invalid', errorMessage)

        navigate(PATHROUTES.AUTH.RESET_PASSWORD)
      }
    },
  })

  useEffect(() => {
    if (!token) {
      navigate(PATHROUTES.AUTH.RESET_PASSWORD)
      return
    }
    verifyMutation.mutate({ token })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const setPasswordMutation = useMutation({
    mutationFn: setNewPassword,
    onSuccess: () => {
      navigate('/auth/password-changed')
    },
  })

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!token) {
      navigate('/auth/reset-password')
      return
    }
    setPasswordMutation.mutate({ token, password: data.password })
  }

  const password = watch('password')

  const passwordRequirements = [
    {
      text: 'Password must be at least 08 characters long.',
      met: password.length >= 8,
    },
    {
      text: 'Password must be combination of lower case, upper case and numbers.',
      met: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password),
    },
    {
      text: 'Password must contain at least one special character.',
      met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
  ]

  return (
    <main className="w-full" aria-label="Create new password page">
      <header className="flex flex-col items-center lg:items-start gap-4 sm:gap-6 w-full">
        <img
          src="/auth/logo.svg"
          alt="SlopeSync logo"
          className="w-28 sm:w-48"
        />
        <div className="flex flex-col items-center lg:items-start gap-2 sm:gap-3 w-full">
          <div className="text-center lg:text-left">
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-neutral-950 uppercase font-[ClashDisplay]">
              CREATE
            </h1>
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-blue-500 uppercase font-[ClashDisplay]">
              new password
            </h1>
          </div>
          <p className="text-sm lg:text-base font-semibold text-ss-neutral-600 text-center lg:text-left">
            Set your new password with minimum 8 characters with a combination
            of letters and numbers
          </p>
        </div>
      </header>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col items-center gap-5 sm:gap-6 w-full"
        aria-label="Create new password form"
        autoComplete="off"
      >
        <div className="flex flex-col gap-4 sm:gap-5 w-full">
          <div className="flex flex-col gap-1.5 w-full">
            <label
              htmlFor="password"
              className="text-sm leading-6 font-medium text-secondary-foreground"
            >
              New Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                inputSize={isMobile ? 'small' : 'large'}
                aria-invalid={!!errors.password}
                aria-describedby={
                  errors.password ? 'password-error' : undefined
                }
                aria-required="true"
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeCloseIcon
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    aria-hidden="true"
                  />
                ) : (
                  <EyeOpenIcon
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
            {errors.password && (
              <span
                className="text-sm text-ss-text-critical"
                id="password-error"
                role="alert"
              >
                {errors.password.message}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:gap-2.5" aria-live="polite">
            {passwordRequirements.map((requirement, index) => (
              <div key={index} className="flex items-start gap-2 sm:gap-3">
                <div
                  className={cn(
                    'w-4 h-4 rounded-4xl bg-ss-neutral-50 flex items-center justify-center mt-0.5 flex-shrink-0',
                    requirement.met
                      ? 'bg-ss-positive-100'
                      : 'bg-ss-action-gray-100'
                  )}
                  aria-hidden="true"
                >
                  <svg
                    className={cn(
                      'w-3 h-3',
                      requirement.met
                        ? 'text-ss-positive-200'
                        : 'text-ss-neutral-200'
                    )}
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span
                  className={cn(
                    'text-xs sm:text-sm leading-relaxed',
                    requirement.met
                      ? 'text-ss-neutral-500 font-normal'
                      : 'text-muted-foreground'
                  )}
                  aria-live="polite"
                >
                  {requirement.text}
                </span>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1.5 w-full">
            <label
              htmlFor="confirmPassword"
              className="text-sm leading-6 font-medium text-secondary-foreground"
            >
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter your password"
                inputSize={isMobile ? 'small' : 'large'}
                aria-invalid={!!errors.confirmPassword}
                aria-describedby={
                  errors.confirmPassword ? 'confirm-password-error' : undefined
                }
                aria-required="true"
                {...register('confirmPassword')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                aria-label={
                  showConfirmPassword
                    ? 'Hide confirm password'
                    : 'Show confirm password'
                }
              >
                {showConfirmPassword ? (
                  <EyeCloseIcon
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    aria-hidden="true"
                  />
                ) : (
                  <EyeOpenIcon
                    className="h-4 w-4 sm:h-5 sm:w-5"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <span
                className="text-sm text-ss-text-critical"
                id="confirm-password-error"
                role="alert"
              >
                {errors.confirmPassword.message}
              </span>
            )}
          </div>
        </div>
        <Button
          type="submit"
          disabled={!isValid || setPasswordMutation.isPending}
          className="w-full"
          size={isMobile ? 'small' : 'large'}
          aria-label="Update password"
        >
          {setPasswordMutation.isPending ? 'Updating...' : 'Update Password'}
        </Button>
      </form>
    </main>
  )
}
